import { ExportFormatEnum } from '@g99-product-web-ui/shared-interface-lib';

import { FILE_EXTENSION } from '../constants/export-modal.constants';

/**
 * Utility function to download a given Blob object as a file.
 *
 * @param blob - The Blob data to be downloaded (e.g., CSV, JSON, Excel).
 * @param format - The export format (file extension) to use for the download.
 *
 * @remarks
 * - Creates a temporary object URL for the Blob.
 * - Dynamically generates a timestamp-based filename.
 * - Triggers a download action using a hidden anchor element.
 * - Cleans up the temporary object URL to free memory.
 *
 * @example
 * ```ts
 * import { downloadBlobAsFile } from './file-utils';
 *
 * const blob = new Blob(['example content'], { type: 'text/plain' });
 * downloadBlobAsFile(blob, 'txt');
 * ```
 */
export function downloadBlobAsFile(
  blob: Blob,
  format: ExportFormatEnum,
  fileName?: string
): void {
  // Create a temporary URL for the blob
  const url = window.URL.createObjectURL(blob);

  // Create a temporary anchor element for download
  const anchor = document.createElement('a');
  anchor.href = url;

  const extension =
    FILE_EXTENSION[format as unknown as keyof typeof FILE_EXTENSION] || '.xls';
  if (fileName) {
    anchor.download = `${fileName}.${extension}`;
  } else {
    // Generate a timestamp for filename uniqueness
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');

    // Set the filename dynamically with format as extension
    anchor.download = `download_${timestamp}.${extension}`;
  }

  // Append to body, trigger download, and remove anchor
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);

  // Clean up the temporary URL
  window.URL.revokeObjectURL(url);
}

/**
 * Utility function to download a given Blob object as a file.
 *
 * @param blob - The Blob data to be downloaded (e.g., CSV, JSON, Excel).
 * @param format - The export format (file extension) to use for the download.
 *
 * @remarks
 * - Creates a temporary object URL for the Blob.
 * - Dynamically generates a timestamp-based filename.
 * - Triggers a download action using a hidden anchor element.
 * - Cleans up the temporary object URL to free memory.
 *
 * @example
 * ```ts
 * import { downloadBlobAsKnowledgeBaseFile } from './file-utils';
 *
 * const blob = new Blob(['example content'], { type: 'text/plain' });
 * downloadBlobAsKnowledgeBaseFile(blob, filename, '.txt');
 * ```
 */
export function downloadBlobAsKnowledgeBaseFile(
  blob: Blob,
  fileName?: string,
  fileExtension?: string
): void {
  // Create a temporary URL for the blob
  const url = window.URL.createObjectURL(blob);

  // Create a temporary anchor element for download
  const anchor = document.createElement('a');
  anchor.href = url;

  const extension = (fileExtension || 'pdf').replace(/^\./, '');
  if (fileName) {
    anchor.download = `${fileName}.${extension}`;
  } else {
    // Generate a timestamp for filename uniqueness
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');

    // Set the filename dynamically with format as extension
    anchor.download = `download_${timestamp}.${extension}`;
  }

  // Append to body, trigger download, and remove anchor
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);

  // Clean up the temporary URL
  window.URL.revokeObjectURL(url);
}
